import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, User, EnvelopeSimple, Phone, Prohibit, ArrowCounterClockwise, Buildings, Warning, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import InvitationLinkDialog from './shared/InvitationLinkDialog';
import { BOUTONS, libelleRole } from '../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog d'administration d'un utilisateur.
 * Props: userId, onClose, onChanged?
 */
export default function UserAdminDialog({ userId, onClose, onChanged }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [lienInvitation, setLienInvitation] = useState(null);
  // RP86 — suppression définitive (« Avancé ») : historique vérifié, puis double confirmation
  const [suppression, setSuppression] = useState(null); // null | {etape, traces, saisie}

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users/${userId}/details`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Impossible de charger la fiche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) load(); /* eslint-disable-next-line */ }, [userId]);

  // RP86 / D12 — « Désactiver le compte » remplace « Bloquer » ; « Réactiver » le rétablit.
  const toggleActive = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const action = user.is_active ? 'desactiver' : 'reactiver';
      const { data } = await axios.post(`${API_URL}/api/admin/users/${user.id}/${action}`, {}, { withCredentials: true });
      toast.success(data?.message || (user.is_active ? 'Compte désactivé' : 'Compte réactivé'));
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const verifierSuppression = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users/${user.id}/historique`, { withCredentials: true });
      setSuppression({ etape: data.suppression_possible ? 1 : 0, traces: data.traces || [], saisie: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Vérification impossible');
    }
  };

  const supprimer = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/users/${user.id}/admin`, { withCredentials: true });
      toast.success('Compte supprimé définitivement');
      onChanged && onChanged();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || 'Suppression impossible');
      setSuppression(null);
    } finally {
      setBusy(false);
    }
  };

  // RP08 — « Renvoyer l'invitation » : lien à usage unique, le mot de passe actuel
  // reste valable tant que le lien n'est pas utilisé (plus de mot de passe régénéré).
  const resendInvitation = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/users/${user.id}/resend-invitation`, {}, { withCredentials: true });
      if (data?.email_sent) toast.success(data.message || `Invitation renvoyée à ${user.email}`);
      else if (data?.activation_url) setLienInvitation(data);
      else toast.info(data?.message || "L'e-mail n'est pas parti");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du renvoi de l'invitation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="user-admin-dialog">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <User size={20} className="text-[#C75B39]" weight="bold" /> Fiche utilisateur
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="user-admin-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {loading && <p className="text-slate-500">Chargement…</p>}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
              <Warning size={16} weight="bold" className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {user && (
            <>
              <div className="mb-5">
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{user.name}</h3>
                <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-600">
                  <span className="flex items-center gap-1"><EnvelopeSimple size={14} /> {user.email}</span>
                  {user.telephone && <span className="flex items-center gap-1"><Phone size={14} /> {user.telephone}</span>}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs ${roleBadge(user.role)}`}>{libelleRole(user.role)}</span>
                  {user.subrole && <span className="text-xs text-slate-500">({user.subrole})</span>}
                </div>
                {user.entreprise_nom && (
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <Buildings size={12} /> Salarié de {user.entreprise_nom}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat label="Statut" value={user.is_active ? 'Actif' : 'Désactivé'} />
                <Stat label="Dernière connexion" value={user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('fr-FR') : 'Jamais'} />
                <Stat label="Connexions" value={user.login_count || 0} />
                <Stat label="Compte créé" value={user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '—'} />
              </div>

              <div className="mb-5">
                <h4 className="text-sm font-semibold mb-2 text-slate-700 uppercase tracking-wide">
                  Chantiers associés ({user.chantiers?.length || 0})
                </h4>
                {(user.chantiers || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucun chantier associé.</p>
                ) : (
                  <div className="space-y-1 border border-slate-200">
                    {user.chantiers.map((c) => (
                      <a
                        key={c.id}
                        href={`/chantiers/${c.id}`}
                        className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0"
                        data-testid={`user-chantier-${c.id}`}
                      >
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-slate-500">{c.address}</div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 ${statusBadge(c.status)}`}>{c.status}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {(user.moe_projects || []).length > 0 && (
                <div className="mb-5">
                  <h4 className="text-sm font-semibold mb-2 text-slate-700 uppercase tracking-wide">
                    Missions MOE ({user.moe_projects.length})
                  </h4>
                  <ul className="text-xs text-slate-600 space-y-1">
                    {user.moe_projects.map((m, i) => (
                      <li key={i}>• {m.mission || m.role} — chantier {m.chantier_id.slice(-6)}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
                <button
                  onClick={resendInvitation}
                  disabled={busy || !user.email}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#1E3A5F] text-[#1E3A5F] bg-white hover:bg-[#1E3A5F] hover:text-white text-sm disabled:opacity-60 transition-colors"
                  data-testid="user-resend-invitation"
                  title={user.email ? `Envoyer à ${user.email} un lien pour choisir son mot de passe` : 'Pas d\'e-mail renseigné'}
                >
                  <EnvelopeSimple size={14} weight="bold" /> Renvoyer l&apos;invitation
                </button>
                <button
                  onClick={toggleActive}
                  disabled={busy}
                  className={`inline-flex items-center gap-2 px-4 py-2 border text-sm ${user.is_active ? 'border-red-200 text-red-700 hover:bg-red-50' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'} disabled:opacity-60`}
                  data-testid="user-toggle-active"
                >
                  {user.is_active ? <Prohibit size={14} /> : <ArrowCounterClockwise size={14} />}
                  {user.is_active ? BOUTONS.desactiverCompte : BOUTONS.reactiverCompte}
                </button>
              </div>
              {(user.welcome_email_sent_at || user.last_resent_at) && (
                <div className="text-xs text-slate-500 mt-3 space-y-0.5" data-testid="user-email-audit">
                  {user.welcome_email_sent_at && (
                    <div>Première invitation : {new Date(user.welcome_email_sent_at).toLocaleString('fr-FR')}</div>
                  )}
                  {user.last_resent_at && user.last_resent_at !== user.welcome_email_sent_at && (
                    <div>Dernier renvoi : {new Date(user.last_resent_at).toLocaleString('fr-FR')}</div>
                  )}
                </div>
              )}

              {/* RP86 / D12 — « Avancé » : suppression définitive réservée à un compte sans
                  historique (créé par erreur), avec double confirmation. */}
              <details className="mt-5 border border-slate-200" data-testid="user-avance">
                <summary className="px-3 py-2 text-sm text-slate-600 cursor-pointer">Avancé</summary>
                <div className="p-3 space-y-2 text-sm">
                  {!suppression && (
                    <button onClick={verifierSuppression} className="inline-flex items-center gap-2 px-3 py-1.5 border border-red-200 text-red-700 hover:bg-red-50"
                      data-testid="user-supprimer-definitivement">
                      <Trash size={14} /> {BOUTONS.supprimerDefinitivement}
                    </button>
                  )}
                  {suppression?.etape === 0 && (
                    <div className="bg-amber-50 border border-amber-200 p-3 text-amber-900" data-testid="user-suppression-refusee">
                      <p className="font-medium">Ce compte a un historique : désactivez-le plutôt.</p>
                      <ul className="list-disc ml-5 text-xs mt-1">
                        {suppression.traces.map((t) => <li key={t.cle}>{t.cle === 'connexions' ? t.libelle : `${t.nombre} ${t.libelle}`}</li>)}
                      </ul>
                      <p className="text-xs mt-1">La désactivation coupe l'accès et conserve ses comptes rendus, ses actions et sa place dans les équipes.</p>
                    </div>
                  )}
                  {suppression?.etape === 1 && (
                    <div className="bg-red-50 border border-red-200 p-3 text-red-900 space-y-2" data-testid="user-suppression-etape-1">
                      <p>Ce compte n'a jamais servi. Le supprimer définitivement ? Cette action est irréversible.</p>
                      <div className="flex gap-2">
                        <button onClick={() => setSuppression(null)} className="px-3 py-1.5 border border-slate-300 bg-white">Annuler</button>
                        <button onClick={() => setSuppression({ ...suppression, etape: 2 })} className="px-3 py-1.5 bg-red-700 text-white" data-testid="user-suppression-continuer">Continuer</button>
                      </div>
                    </div>
                  )}
                  {suppression?.etape === 2 && (
                    <div className="bg-red-50 border border-red-200 p-3 text-red-900 space-y-2" data-testid="user-suppression-etape-2">
                      <p>Pour confirmer, saisissez l'adresse e-mail du compte : <strong>{user.email}</strong></p>
                      <input value={suppression.saisie} onChange={(e) => setSuppression({ ...suppression, saisie: e.target.value })}
                        className="w-full border border-red-200 px-2 py-1.5 bg-white" data-testid="user-suppression-saisie" />
                      <div className="flex gap-2">
                        <button onClick={() => setSuppression(null)} className="px-3 py-1.5 border border-slate-300 bg-white">Annuler</button>
                        <button onClick={supprimer} disabled={busy || suppression.saisie.trim().toLowerCase() !== (user.email || '').toLowerCase()}
                          className="px-3 py-1.5 bg-red-700 text-white disabled:opacity-50" data-testid="user-suppression-confirmer">
                          {BOUTONS.supprimerDefinitivement}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </details>
            </>
          )}
        </div>
        {/* dans le bloc qui stoppe la propagation : un clic dans la fenêtre du lien ne ferme pas la fiche */}
        <InvitationLinkDialog invitation={lienInvitation} onClose={() => setLienInvitation(null)} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-3 bg-slate-50 border border-slate-200">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="font-semibold text-slate-900 mt-0.5 text-sm">{value}</p>
    </div>
  );
}

function roleBadge(role) {
  const map = {
    admin: 'bg-purple-100 text-purple-700',
    architect: 'bg-[#FFF2EB] text-[#C75B39]',
    amo: 'bg-blue-100 text-blue-700',
    intervenant: 'bg-slate-100 text-slate-700',
    client: 'bg-amber-100 text-amber-800',
    entreprise: 'bg-emerald-100 text-emerald-800',
    employe_entreprise: 'bg-teal-100 text-teal-800',
  };
  return map[role] || 'bg-slate-100 text-slate-700';
}

function statusBadge(status) {
  const map = {
    en_cours: 'bg-emerald-100 text-emerald-700',
    prospection: 'bg-amber-100 text-amber-700',
    termine: 'bg-slate-100 text-slate-700',
    en_pause: 'bg-blue-100 text-blue-700',
  };
  return map[status] || 'bg-slate-100 text-slate-700';
}
